import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Progress,
  List,
  Tag,
  Alert,
  message,
  Spin,
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  PlusOutlined,
  PhoneOutlined,
  CalendarOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAdvisorDashboardStats, AdvisorDashboardStats } from '../../services/statsApi';

const { Title, Text } = Typography;

const AdvisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdvisorDashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getAdvisorDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      setLoadError(true);
      message.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Active Estimates',
      value: stats?.activeEstimates || 0,
      icon: <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      color: '#e6f7ff',
    },
    {
      title: 'Monthly Sales',
      value: `$${(stats?.monthlySales || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: <DollarOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      color: '#f6ffed',
    },
    {
      title: 'Customer Contacts',
      value: stats?.customerContacts || 0,
      icon: <PhoneOutlined style={{ fontSize: 24, color: '#faad14' }} />,
      color: '#fffbe6',
    },
    {
      title: 'This Month',
      value: stats?.customersThisMonth || 0,
      prefix: '',
      suffix: 'customers',
      icon: <UserOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      color: '#e6fffb',
    },
  ];

  const getStatusColor = (status?: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      sent: 'processing',
      viewed: 'cyan',
      approved: 'success',
      declined: 'error',
    };
    return colors[status || ''] || 'default';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', gap: 16 }}>
        <Spin size="large" />
        <Text type="secondary">Loading dashboard...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={2}>Advisor Dashboard</Title>
          <Text type="secondary">
            Welcome back, {user?.first_name}! Manage your estimates and customer relationships.
          </Text>
        </div>

        {loadError && (
          <Alert
            type="error"
            showIcon
            message="Couldn't load dashboard data"
            description="Showing zeros. Check your connection and retry."
            action={
              <Button size="small" onClick={loadDashboardStats}>
                Retry
              </Button>
            }
          />
        )}

        {/* Stats Cards */}
        <Row gutter={[16, 16]}>
          {statCards.map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: stat.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {stat.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/advisor/estimates/new')}
            >
              New Estimate
            </Button>
            <Button
              icon={<UserOutlined />}
              onClick={() => navigate('/advisor/customers')}
            >
              Manage Customers
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => navigate('/advisor/estimates')}
            >
              View All Estimates
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate('/advisor/appointments')}
            >
              Schedule Appointment
            </Button>
          </Space>
        </Card>

        {/* Main Content */}
        <Row gutter={[16, 16]}>
          {/* Active Estimates */}
          <Col xs={24} lg={14}>
            <Card
              title="Recent Estimates"
              extra={
                <Button
                  type="link"
                  onClick={() => navigate('/advisor/estimates')}
                >
                  View All
                </Button>
              }
            >
              <List
                dataSource={stats?.recentEstimates || []}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        onClick={() => navigate(`/advisor/estimates/${item.id}`)}
                      >
                        View
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{item.customer_name}</Text>
                          <Tag color={getStatusColor(item.status)}>
                            {(item.status || '').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">{item.vehicle}</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {formatCurrency(item.total_amount)}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Performance & Follow-ups */}
          <Col xs={24} lg={10}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Monthly Performance */}
              <Card title={<><TrophyOutlined /> Monthly Performance</>}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text>Sales Goal</Text>
                      <Text strong>
                        ${(stats?.monthlySales || 0).toLocaleString()} / ${(stats?.salesGoal || 0).toLocaleString()}
                      </Text>
                    </div>
                    <Progress percent={Math.round(stats?.salesProgress || 0)} status="active" />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text>Estimates Created</Text>
                      <Text strong>{stats?.estimatesCreated || 0} / {stats?.estimatesGoal || 0}</Text>
                    </div>
                    <Progress
                      percent={Math.round(((stats?.estimatesCreated || 0) / (stats?.estimatesGoal || 1)) * 100)}
                      status="active"
                      strokeColor="#52c41a"
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text>Conversion Rate</Text>
                      <Text strong>{(stats?.conversionRate || 0).toFixed(0)}%</Text>
                    </div>
                    <Progress percent={Math.round(stats?.conversionRate || 0)} strokeColor="#faad14" />
                  </div>
                </Space>
              </Card>

              {/* Follow-ups */}
              <Card title="Pending Follow-ups">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">
                    {stats?.pendingFollowUps || 0} estimates pending follow-up
                  </Text>
                </div>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Commission Info */}
        <Card title="Commission Summary">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title="This Month"
                value={(stats?.commissionMonth || 0).toFixed(2)}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="This Quarter"
                value={(stats?.commissionQuarter || 0).toFixed(2)}
                prefix="$"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Year to Date"
                value={(stats?.commissionYTD || 0).toFixed(2)}
                prefix="$"
              />
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};

export default AdvisorDashboard;
